import { normalizeTaskResponse } from "../task/normalize";
import { buildUrl, defaultFetchHeaders, fetchJson } from "../../../utils";
import {
  Task,
  CommissionBankSettings,
  CommissionPaymentMethodSummary,
  CommissionPayoutHistoryPage,
  CommissionReportFilters,
  CommissionReportSummary,
} from "../../types";
import { centsFromApi } from "../../currency";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const commissionBase = `${backend}/commission`;

type ApiRecord = Record<string, unknown>;

const field = (value: ApiRecord, camel: string, snake: string) =>
  value[camel] ?? value[snake];

const integerFromApi = (value: unknown, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value !== "number" || !Number.isSafeInteger(value))
    throw new Error("Commission API value must be a safe integer");
  return value;
};

const optionalString = (value: unknown) =>
  typeof value === "string" && value ? value : null;

export const normalizeCommissionReportSummary = (
  value: ApiRecord,
): CommissionReportSummary => ({
  earnedCents: centsFromApi(field(value, "earnedCents", "earned_cents")),
  paidCents: centsFromApi(field(value, "paidCents", "paid_cents")),
  currentOutstandingCents: centsFromApi(
    field(value, "currentOutstandingCents", "current_outstanding_cents"),
  ),
  accrualCount: integerFromApi(field(value, "accrualCount", "accrual_count")),
  payoutCount: integerFromApi(field(value, "payoutCount", "payout_count")),
});

export const normalizeCommissionCashFlow = (
  values: ApiRecord[],
): CommissionPaymentMethodSummary[] => values.map((value) => ({
  paymentType: optionalString(field(value, "paymentType", "payment_type")) as
    CommissionPaymentMethodSummary["paymentType"],
  earnedCents: centsFromApi(field(value, "earnedCents", "earned_cents")),
  paidCents: centsFromApi(field(value, "paidCents", "paid_cents")),
  currentOutstandingCents: centsFromApi(
    field(value, "currentOutstandingCents", "current_outstanding_cents"),
  ),
}));

const normalizePayout = (value: ApiRecord): CommissionPayoutHistoryPage["items"][number] => ({
  id: String(value.id || ""),
  accountId: optionalString(field(value, "accountId", "account_id")),
  taskId: String(field(value, "taskId", "task_id") || ""),
  locationId: optionalString(field(value, "locationId", "location_id")),
  locationName: optionalString(field(value, "locationName", "location_name")),
  machineId: optionalString(field(value, "machineId", "machine_id")),
  machineName: optionalString(field(value, "machineName", "machine_name")),
  payoutCents: centsFromApi(field(value, "payoutCents", "payout_cents")),
  paymentType: optionalString(field(value, "paymentType", "payment_type")) as
    CommissionPayoutHistoryPage["items"][number]["paymentType"],
  status: String(value.status || ""),
  reason: String(value.reason || ""),
  paidAt: String(field(value, "paidAt", "paid_at") || ""),
  allocationCount: integerFromApi(
    field(value, "allocationCount", "allocation_count"),
  ),
  historical: Boolean(value.historical),
});

export const normalizeCommissionPayoutHistory = (
  value: ApiRecord,
): CommissionPayoutHistoryPage => ({
  items: Array.isArray(value.items)
    ? value.items.map((item) => normalizePayout(item as ApiRecord))
    : [],
  nextCursor: optionalString(field(value, "nextCursor", "next_cursor")),
});

const filterParams = (filters: CommissionReportFilters) => ({
  paymentTypes: filters.paymentTypes,
  startDate: filters.startDate,
  endDate: filters.endDate,
  locationId: filters.locationId,
  machineId: filters.machineId,
});

export async function fetchCommissionReportSummary(
  filters: CommissionReportFilters = {},
  signal?: AbortSignal,
): Promise<CommissionReportSummary> {
  const value = await fetchJson<ApiRecord>(
    buildUrl(`${commissionBase}/reports/summary`, filterParams(filters)),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      signal,
    },
    "Failed to load commission summary",
  );
  return normalizeCommissionReportSummary(value);
}

export async function fetchCommissionCashFlow(
  filters: CommissionReportFilters = {},
  signal?: AbortSignal,
): Promise<CommissionPaymentMethodSummary[]> {
  const values = await fetchJson<ApiRecord[]>(
    buildUrl(`${commissionBase}/reports/cash-flow`, filterParams(filters)),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      signal,
    },
    "Failed to load commission cash flow",
  );
  return normalizeCommissionCashFlow(values);
}

export async function fetchCommissionPayouts(
  filters: CommissionReportFilters & {
    cursor?: string;
    limit?: number;
  } = {},
  signal?: AbortSignal,
): Promise<CommissionPayoutHistoryPage> {
  const value = await fetchJson<ApiRecord>(
    buildUrl(`${commissionBase}/payouts`, {
      ...filterParams(filters),
      cursor: filters.cursor,
      limit: filters.limit,
    }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      signal,
    },
    "Failed to load commission payouts",
  );
  return normalizeCommissionPayoutHistory(value);
}

export type { CommissionBankSettings } from "../../types";
export type CommissionPaymentReview = CommissionBankSettings & {
  activityAt?: string | null;
  rowId?: string | null;
  taskId?: string | null;
  machineName?: string | null;
  paymentType?: string | null;
  paidAt?: string | null;
  accountId: string;
  canApprove?: boolean;
  canEditRecipient?: boolean;
  canResume?: boolean;
  paymentHistory?: { label: string; at: string; detail?: string }[];
  expenseId?: string | null;
  locationId: string | null;
  name: string;
  amountCents: number;
  status: "needs_approval" | "processing" | "needs_attention" | "scheduled" | "paid" | "cancelled" | "reversed";
  due: boolean;
  aboveLimit: boolean;
  payFrom: string | null;
  depositTo: string | null;
  recipient: string | null;
  setupError: string;
  failureMessage: string;
  processingEnabled: boolean;
  reviewToken: string;
  willSendNow?: boolean;
  suggestedFundingAccountId?: string | null;
  suggestedPayoutAccountId?: string | null;
  monthlyFeeCents?: number | null;
  monthlyFeeExempt?: boolean;
  directDepositEnabled?: boolean;
  allocations: { id: string; taskId: string | null; machineName?: string | null; date: string; amountCents: number }[];
  fundingAccounts?: { value: string; label: string }[];
  payoutAccounts?: { value: string; label: string }[];
};

export async function commissionPaymentRequest<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return fetchJson<T>(`${commissionBase}${path}`, {
    method: body === undefined ? "GET" : "POST", credentials: "include",
    headers: await defaultFetchHeaders(), signal,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }, "Unable to update commission payment");
}

export const fetchCommissionPaymentSettings = (scope: { locationId?: string; machineId?: string }, signal?: AbortSignal) =>
  commissionPaymentRequest<CommissionPaymentReview>(`/payment-settings?${new URLSearchParams(Object.entries(scope).filter((entry): entry is [string, string] => Boolean(entry[1])))}`, undefined, signal);

export type CommissionTaskExpenseLink = {
  expenseId: string;
  paymentId: string;
  amountCents: number;
  status: CommissionPaymentReview["status"];
  canPay: boolean;
};


export async function recordCommissionPayment(taskId: string, data: {
  paidOn: string;
  paymentType: NonNullable<Task["commissionPaymentType"]>;
  amountCents: number;
}): Promise<Task> {
  return normalizeTaskResponse(await commissionPaymentRequest<Parameters<typeof normalizeTaskResponse>[0]>(`/payments/for-task/${taskId}/record`, data));
}
